import { useCallback } from 'react';

import { useModal } from '../context/AccountModalContext';
import { useAuth } from '../context/AuthContext';
import { useSubscriptionAccess } from '../context/SubscriptionAccessContext';
import { checkEnv } from '../utils/Utils';

/**
 * Checks if the user is authenticated and if they should be shown the upgrade
 * modal, given the number of days left on their subscription.
 *
 * @returns {function} A function that checks authentication and shows the
 * upgrade modal, if necessary.
 */
const useAuthCheck = () => {
    const { isAuthenticated, user } = useAuth();
    const { toggleModalAccount, toggleUpgradeModal } = useModal();
    const { subscriptionData } = useSubscriptionAccess();
    // TODO: Check if we are in production and set maximum days for upgrade modal need to be remove after testing
    const isProd = checkEnv();
    const maxTrialDays = isProd ? 30 : 200; // Maximum Trial days to show upgrade modal
    const maxMonthlyDays = isProd ? 7 : 35; // Maximum Monthly days to show upgrade modal
    const maxAnnualDays = isProd ? 30 : 366; // Maximum Annual days to show upgrade modal

    const checkAuthentication = useCallback(() => {
        // Not authenticated — show login
        if (!isAuthenticated || !user) {
            toggleModalAccount();
            return false;
        }

        const today = new Date().toDateString();
        const dismissedToday =
            localStorage.getItem('trialDismissedOn') === today;
        const daysLeft = subscriptionData?.daysLeft ?? null;

        // Upgrade modal
        const shouldShowUpgradeModal =
            !dismissedToday &&
            daysLeft !== null &&
            ((subscriptionData?.isTrial && daysLeft <= maxTrialDays) ||
                (subscriptionData?.isMonthly && daysLeft <= maxMonthlyDays) ||
                (subscriptionData?.isAnnual && daysLeft <= maxAnnualDays));

        if (shouldShowUpgradeModal) {
            toggleUpgradeModal();
        }

        return true;
    }, [
        isAuthenticated,
        user,
        toggleModalAccount,
        toggleUpgradeModal,
        subscriptionData?.normalizedName,
        subscriptionData?.daysLeft,
    ]);

    return checkAuthentication;
};

export default useAuthCheck;
